/**
 * Adapter mock — dev local via BroadcastChannel + localStorage.
 *
 * Duas abas no mesmo browser conversam sem rede. Master num, player noutro.
 * Estado persiste em localStorage para sobreviver a refresh.
 *
 * NÃO é um "cliente do mock do servidor"; é um substituto do servidor
 * rodando localmente na aba do master. Para WS real, ver adapter/websocket.ts
 * (Fase 3).
 *
 * Ver docs/module_lab/ADAPTER_CONTRACT.md §Mock adapter particularidades.
 */

import type {
  Adapter,
  AssignmentEntry,
  InteractionMode,
  LabComponentStateSnapshot,
  OwnResponseEntry,
  PairedResponseEntry,
  PostitEntry,
  QuizStateSnapshot,
  RevealedResponse,
  ScoreSnapshot,
} from '../types/adapter'
import type { Role } from '../types/session'
import type { TelemetryEvent } from '../types/events'

interface MockState {
  currentSlideIndex: number
  currentActivityId: string | null
  interactionMode: InteractionMode
  quizzes: Record<string, QuizStateSnapshot>
  scores: Record<string, number> // membershipId → total
  /** Respostas previas do proprio aluno (lab.form.openQuestion, lab.assess.confidence). */
  pairedResponses: Record<string, PairedResponseEntry>
  /** Resposta do proprio aluno por slideIndex (string) — repreenche ao voltar. PR-4. */
  ownResponsesBySlide: Record<string, OwnResponseEntry>
  /** Respostas openQuestion liberadas pelo mestre por slideIndex (string). PR-4. */
  revealedBySlide: Record<string, RevealedResponse[]>
  /** Estado agregado por key `${missionId}:${slideIndex}` — pra paineis de mestre. */
  labComponentStates: Record<string, LabComponentStateSnapshot>
  /** Submissoes brutas indexadas por slide+missionId pra agregacao no master. */
  labSubmissions: Record<string, Array<Record<string, unknown>>>
  /** Atribuicoes de equipe por slideIndex (string). PR-3. */
  myAssignmentBySlide: Record<string, AssignmentEntry>
  /** Postits por slideIndex (string). PR-3. */
  postitsBySlide: Record<string, PostitEntry[]>
}

type Listener = (payload: any) => void

const EVENT_LOG_KEY = 'module_lab:events'
const EVENT_LOG_MAX = 2000

function stateKey(sessionId: string): string {
  return `module_lab:mock-state:${sessionId}`
}

function loadState(sessionId: string): MockState {
  const fallback: MockState = {
    currentSlideIndex: 0,
    currentActivityId: null,
    interactionMode: 'free',
    quizzes: {},
    scores: {},
    pairedResponses: {},
    ownResponsesBySlide: {},
    revealedBySlide: {},
    labComponentStates: {},
    labSubmissions: {},
    myAssignmentBySlide: {},
    postitsBySlide: {},
  }
  try {
    const raw = localStorage.getItem(stateKey(sessionId))
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<MockState>
      // Estado salvo de versao anterior pode nao ter os campos novos.
      return { ...fallback, ...parsed }
    }
  } catch {
    /* ignora */
  }
  return fallback
}

function saveState(sessionId: string, state: MockState): void {
  try {
    localStorage.setItem(stateKey(sessionId), JSON.stringify(state))
  } catch {
    /* ignora quota cheia */
  }
}

function pushEventLog(event: Record<string, unknown>): void {
  try {
    const raw = localStorage.getItem(EVENT_LOG_KEY)
    const log: Array<Record<string, unknown>> = raw ? JSON.parse(raw) : []
    log.push(event)
    if (log.length > EVENT_LOG_MAX) {
      log.splice(0, log.length - EVENT_LOG_MAX)
    }
    localStorage.setItem(EVENT_LOG_KEY, JSON.stringify(log))
  } catch {
    /* quota */
  }
}

export function createMockAdapter(params: {
  sessionId: string
  userId: string
  role: Role
}): Adapter {
  const { sessionId, userId, role } = params

  const channel = new BroadcastChannel(`module_lab:${sessionId}`)
  const listeners = new Map<string, Set<Listener>>()
  const state = loadState(sessionId)

  function emit(event: string, payload: any): void {
    listeners.get(event)?.forEach((cb) => {
      try {
        cb(payload)
      } catch (err) {
        console.error('[module_lab/mock] listener error', err)
      }
    })
  }

  function broadcast(msg: Record<string, unknown>): void {
    channel.postMessage(msg)
  }

  function persist(): void {
    saveState(sessionId, state)
  }

  function applyScoreDelta(membershipId: string, delta: number): void {
    state.scores[membershipId] = (state.scores[membershipId] ?? 0) + delta
  }

  channel.onmessage = (ev: MessageEvent) => {
    const msg = ev.data as Record<string, unknown> | null
    if (!msg || typeof msg !== 'object') return
    const type = msg.type as string
    if (!type) return

    if (type === 'slideChange') {
      state.currentSlideIndex = msg.index as number
      persist()
      emit('slideChange', { index: msg.index })
    } else if (type === 'activityChange') {
      state.currentActivityId = (msg.activityId as string) ?? null
      persist()
      emit('activityChange', { activityId: state.currentActivityId })
    } else if (type === 'interactionModeChange') {
      state.interactionMode = (msg.mode as InteractionMode) ?? 'free'
      persist()
      emit('interactionModeChange', { mode: state.interactionMode })
    } else if (type === 'quizState') {
      const qs = msg as unknown as QuizStateSnapshot
      state.quizzes[qs.questionId] = qs
      persist()
      emit('quizState', qs)
    } else if (type === 'quizAnswer' && role === 'master') {
      // Master agrega. Player só recebe quizState.
      const q = state.quizzes[msg.questionId as string]
      if (!q || q.status !== 'open') return
      const answerIndex = msg.answerIndex as number
      q.distribution[answerIndex] = (q.distribution[answerIndex] ?? 0) + 1
      q.responses = (q.responses ?? 0) + 1
      persist()
      broadcast({ type: 'quizState', ...q })
      emit('quizState', q)
    } else if (type === 'scoreUpdate') {
      const deltas = (msg.deltas as Array<{ membershipId: string; delta: number }>) ?? []
      for (const d of deltas) applyScoreDelta(d.membershipId, d.delta)
      persist()
      emit('scoreUpdate', msg)
    } else if (type === 'requestSync' && role === 'master') {
      broadcast({ type: 'slideChange', index: state.currentSlideIndex })
      if (state.currentActivityId !== null) {
        broadcast({ type: 'activityChange', activityId: state.currentActivityId })
      }
      for (const q of Object.values(state.quizzes)) {
        broadcast({ type: 'quizState', ...q })
      }
    } else if (type === 'labSubmit') {
      // Mensagem cruzada — outra aba (player) submeteu algo de lab.*
      applyLabSubmissionLocally(msg)
    } else if (type === 'labComponentState') {
      const snap = msg as unknown as LabComponentStateSnapshot
      state.labComponentStates[`${snap.missionId}:${snap.slideIndex}`] = snap
      persist()
      emit('labComponentState', snap)
    } else if (type === 'assignmentReceived') {
      const slideIndex = msg.slideIndex as number
      const teamId = msg.teamId as string
      const payload = (msg.myAssignment as Record<string, unknown>) ?? {}
      state.myAssignmentBySlide[String(slideIndex)] = { teamId, payload }
      persist()
      emit('assignmentReceived', { slideIndex, teamId, payload })
    } else if (type === 'postitChange') {
      applyPostitChangeLocally(msg)
    } else if (type === 'openQuestionRevealed') {
      applyRevealLocally(msg)
    }
  }

  function applyRevealLocally(msg: Record<string, unknown>): void {
    const key = String(msg.slideIndex as number)
    const membershipId = msg.membershipId as string
    const revealed = msg.revealed as boolean
    const list = state.revealedBySlide[key] ?? []
    const idx = list.findIndex((r) => r.membershipId === membershipId)
    if (revealed) {
      const entry: RevealedResponse = {
        membershipId,
        displayName: (msg.displayName as string) ?? 'Aluno',
        pairId: msg.pairId as string,
        value: msg.value as string,
      }
      if (idx >= 0) list[idx] = entry
      else list.push(entry)
    } else if (idx >= 0) {
      list.splice(idx, 1)
    }
    state.revealedBySlide[key] = list
    persist()
    emit('openQuestionRevealed', msg)
  }

  function applyPostitChangeLocally(msg: Record<string, unknown>): void {
    const action = msg.action as string
    if (action === 'deleted') {
      const id = msg.postitId as string
      for (const list of Object.values(state.postitsBySlide)) {
        const idx = list.findIndex((p) => p.id === id)
        if (idx >= 0) list.splice(idx, 1)
      }
    } else {
      const p = msg.postit as PostitEntry | undefined
      if (p && typeof p.slideIndex === 'number') {
        const key = String(p.slideIndex)
        const list = state.postitsBySlide[key] ?? []
        const idx = list.findIndex((x) => x.id === p.id)
        if (idx >= 0) {
          list[idx] = p
        } else {
          list.push(p)
        }
        state.postitsBySlide[key] = list
      }
    }
    persist()
    emit('postitChange', msg)
  }

  /**
   * Aplica uma submissão lab.* local + recalcula aggregate + broadcast.
   * Chamado tanto pelo submit local do próprio adapter quanto pelo handler
   * de mensagens cruzadas. Em preview (1 aba só) usa o caminho local.
   */
  function applyLabSubmissionLocally(msg: Record<string, unknown>): void {
    const missionId = msg.missionId as string
    const slideIndex = msg.slideIndex as number
    const membershipId = (msg.membershipId as string) ?? `mock-${userId}`
    const payload = (msg.payload as Record<string, unknown>) ?? {}

    if (!missionId || typeof slideIndex !== 'number') return

    const key = `${missionId}:${slideIndex}`
    const list = state.labSubmissions[key] ?? []
    // Dedup por membership_id + chave natural especifica de cada tipo.
    const naturalKey = naturalKeyFor(missionId, payload, membershipId)
    const filtered = list.filter(
      (s) => naturalKeyFor(missionId, s.payload as Record<string, unknown>,
                          s.membershipId as string) !== naturalKey,
    )
    filtered.push({ membershipId, payload, ts: Date.now() })
    state.labSubmissions[key] = filtered

    // pairedResponses + ownResponsesBySlide do proprio aluno (so no submit dele)
    if (membershipId === `mock-${userId}`) {
      if (missionId === 'lab.form.openQuestion' && payload.pairId) {
        state.pairedResponses[payload.pairId as string] = {
          value: payload.value as string,
          slideIndex,
          kind: 'openQuestion',
        }
        state.ownResponsesBySlide[String(slideIndex)] = {
          kind: 'openQuestion',
          pairId: payload.pairId as string,
          value: payload.value as string,
        }
      } else if (missionId === 'lab.assess.confidence' && payload.pairId) {
        state.pairedResponses[payload.pairId as string] = {
          value: payload.value as number,
          slideIndex,
          kind: 'confidence',
        }
        state.ownResponsesBySlide[String(slideIndex)] = {
          kind: 'confidence',
          pairId: payload.pairId as string,
          value: payload.value as number,
        }
      }
    }

    // Recalcula aggregate como o backend faria
    const aggregate = buildAggregate(missionId, filtered)
    const snap: LabComponentStateSnapshot = {
      missionId,
      slideIndex,
      aggregate,
      totalPlayers: 1, // preview mock — placeholder
    }
    state.labComponentStates[key] = snap
    persist()
    emit('labComponentState', snap)
    broadcast({ type: 'labComponentState', ...snap })
  }

  function naturalKeyFor(
    missionId: string,
    payload: Record<string, unknown>,
    membershipId: string,
  ): string {
    if (missionId === 'lab.assess.quizJustified') {
      return `${membershipId}:${payload.questionId}`
    }
    if (missionId === 'lab.form.openQuestion' || missionId === 'lab.assess.confidence') {
      return `${membershipId}:${payload.pairId}`
    }
    if (missionId === 'lab.form.structured') {
      // structured: cada submit substitui tudo
      return `${membershipId}:structured`
    }
    return `${membershipId}:${JSON.stringify(payload)}`
  }

  function buildAggregate(
    missionId: string,
    subs: Array<Record<string, unknown>>,
  ): Record<string, unknown> {
    if (missionId === 'lab.assess.confidence') {
      return {
        responses: subs.map((s) => {
          const p = s.payload as Record<string, unknown>
          return { membershipId: s.membershipId, value: p.value }
        }),
        count: subs.length,
      }
    }
    if (missionId === 'lab.assess.quizJustified') {
      return {
        responses: subs.map((s) => {
          const p = s.payload as Record<string, unknown>
          return {
            membershipId: s.membershipId,
            questionId: p.questionId,
            optionId: p.optionId,
            isCorrect: p.isCorrect,
            justification: p.justification,
          }
        }),
        count: subs.length,
      }
    }
    if (missionId === 'lab.form.openQuestion') {
      return {
        responses: subs.map((s) => {
          const p = s.payload as Record<string, unknown>
          return {
            membershipId: s.membershipId,
            fieldId: p.pairId,
            value: p.value,
            submitted: true,
          }
        }),
        count: subs.length,
      }
    }
    if (missionId === 'lab.form.structured') {
      // expande para uma linha por field, simulando o backend
      const flattened: Array<Record<string, unknown>> = []
      for (const s of subs) {
        const p = s.payload as Record<string, unknown>
        const values = (p.values as Record<string, string>) ?? {}
        for (const [fieldId, value] of Object.entries(values)) {
          flattened.push({
            membershipId: s.membershipId,
            fieldId,
            value,
            submitted: true,
          })
        }
      }
      return { responses: flattened, count: flattened.length }
    }
    return { responses: [], count: 0 }
  }

  // Aluno que entra pede snapshot. Adiado para o próximo macrotask porque o
  // listener do React é registrado em useEffect (que roda após o render) —
  // se mandássemos o requestSync síncrono aqui, a resposta broadcastada pelo
  // master poderia chegar antes do listener existir.
  if (role === 'player') {
    setTimeout(() => broadcast({ type: 'requestSync', userId }), 0)
  }

  // Em mock, a role da URL é a "real" (não há backend autoritativo).
  // Emit sessionSnapshot sintético pra que RoleGate/useSessionState
  // recebam actualRole e desbloqueiem a UI.
  setTimeout(() => {
    emit('sessionSnapshot', {
      type: 'sessionSnapshot',
      sessionId,
      gameSlug: 'hello-world', // fallback — LabRuntime faz lookup REST pelo slug real
      currentSlideIndex: state.currentSlideIndex,
      currentActivityId: state.currentActivityId,
      interactionMode: state.interactionMode,
      status: 'live',
      role,
      membershipId: `mock-${userId}`,
      quizzes: state.quizzes,
      participants: [],
      scores: [],
      pairedResponses: state.pairedResponses,
      ownResponsesBySlide: state.ownResponsesBySlide,
      revealedBySlide: state.revealedBySlide,
      myAssignmentBySlide: state.myAssignmentBySlide,
      postitsBySlide: state.postitsBySlide,
    })
  }, 0)

  // dispara connected assíncrono pra dar tempo de subscrever
  queueMicrotask(() => emit('connected', undefined))

  const adapter: Adapter = {
    sessionId,
    userId,
    role,

    on(event, cb) {
      if (!listeners.has(event)) listeners.set(event, new Set())
      listeners.get(event)!.add(cb)
      return () => {
        listeners.get(event)?.delete(cb)
      }
    },

    getCurrentSlideIndex: () => state.currentSlideIndex,
    getCurrentActivityId: () => state.currentActivityId,
    getInteractionMode: () => state.interactionMode,
    getQuizState: (questionId) => state.quizzes[questionId] ?? null,
    getScore: (membershipId): ScoreSnapshot => {
      if (!membershipId) return { total: 0 }
      return { total: state.scores[membershipId] ?? 0 }
    },
    getPairedResponse: (pairId) => state.pairedResponses[pairId] ?? null,
    getOwnResponse: (slideIndex) => state.ownResponsesBySlide[String(slideIndex)] ?? null,
    getRevealedResponses: (slideIndex) => state.revealedBySlide[String(slideIndex)] ?? [],
    getLabComponentState: (missionId, slideIndex) =>
      state.labComponentStates[`${missionId}:${slideIndex}`] ?? null,
    getMyAssignment: (slideIndex) =>
      state.myAssignmentBySlide[String(slideIndex)] ?? null,
    getPostits: (slideIndex) =>
      state.postitsBySlide[String(slideIndex)] ?? [],

    setSlide(index) {
      if (role !== 'master') return
      state.currentSlideIndex = index
      persist()
      broadcast({ type: 'slideChange', index })
      emit('slideChange', { index })
    },

    setActivity(activityId) {
      if (role !== 'master') return
      state.currentActivityId = activityId
      persist()
      broadcast({ type: 'activityChange', activityId })
      emit('activityChange', { activityId })
    },

    setInteractionMode(mode) {
      if (role !== 'master') return
      if (state.interactionMode === mode) return
      state.interactionMode = mode
      persist()
      broadcast({ type: 'interactionModeChange', mode })
      emit('interactionModeChange', { mode })
    },

    openQuiz({ questionId, options, correctIndex }) {
      if (role !== 'master') return
      const q: QuizStateSnapshot = {
        questionId,
        status: 'open',
        distribution: new Array(options.length).fill(0),
        responses: 0,
        correctIndex: null,
        optionsCount: options.length,
      }
      state.quizzes[questionId] = q
      // guarda a correct pra revelar no close
      ;(q as QuizStateSnapshot & { _correctIndex: number })._correctIndex = correctIndex
      persist()
      broadcast({ type: 'quizState', ...q })
      emit('quizState', q)
    },

    closeQuiz(questionId) {
      if (role !== 'master') return
      const q = state.quizzes[questionId] as (QuizStateSnapshot & { _correctIndex?: number }) | undefined
      if (!q) return
      q.status = 'closed'
      q.correctIndex = q._correctIndex ?? null
      persist()
      broadcast({ type: 'quizState', ...q })
      emit('quizState', q)
    },

    resetQuiz(questionId) {
      if (role !== 'master') return
      delete state.quizzes[questionId]
      persist()
      const msg: QuizStateSnapshot = {
        questionId,
        status: 'idle',
        distribution: [],
        responses: 0,
        correctIndex: null,
      }
      broadcast({ type: 'quizState', ...msg })
      emit('quizState', msg)
    },

    adjustScore({ membershipId, delta, reason }) {
      if (role !== 'master') return
      applyScoreDelta(membershipId, delta)
      persist()
      const payload = {
        type: 'scoreUpdate',
        deltas: [{ membershipId, delta, reason, source: 'master_override' as const }],
      }
      broadcast(payload)
      emit('scoreUpdate', payload)
    },

    submitAnswer(questionId, answerIndex) {
      broadcast({ type: 'quizAnswer', questionId, answerIndex, userId })
    },

    submitProgress({ slideId, progress }) {
      broadcast({ type: 'progress', slideId, progress, userId })
    },

    logEvent(event: TelemetryEvent) {
      const stamped = {
        ...event,
        sessionId,
        userId,
        role,
        slideIndex: state.currentSlideIndex,
        activityId: state.currentActivityId,
        ts: Date.now(),
      }
      pushEventLog(stamped)
      // em dev, imprime pra facilitar inspeção
      if (typeof console !== 'undefined') {
        console.debug('[module_lab/event]', stamped)
      }
    },

    submitOpenQuestion({ pairId, slideIndex, value }) {
      const submission = {
        type: 'labSubmit',
        missionId: 'lab.form.openQuestion',
        slideIndex,
        membershipId: `mock-${userId}`,
        payload: { pairId, value },
      }
      applyLabSubmissionLocally(submission)
      broadcast(submission)
      emit('openQuestionAck', { pairId, slideIndex, ts: Date.now() })
    },

    revealOpenQuestion({ slideIndex, pairId, membershipId, revealed }) {
      // Em mock o valor vem das submissões locais do slide.
      const subs = state.labSubmissions[`lab.form.openQuestion:${slideIndex}`] ?? []
      const sub = subs.find((s) => (s.membershipId as string) === membershipId)
      const value = ((sub?.payload as { value?: string } | undefined)?.value) ?? ''
      const flat = {
        type: 'openQuestionRevealed',
        slideIndex, pairId, membershipId, value, revealed,
        displayName: membershipId,  // mock nao tem nome real — usa o id
      }
      applyRevealLocally(flat)
      broadcast(flat)
    },

    submitConfidence({ pairId, slideIndex, value }) {
      const submission = {
        type: 'labSubmit',
        missionId: 'lab.assess.confidence',
        slideIndex,
        membershipId: `mock-${userId}`,
        payload: { pairId, value },
      }
      applyLabSubmissionLocally(submission)
      broadcast(submission)
      emit('confidenceAck', { pairId, slideIndex, value, ts: Date.now() })
    },

    submitQuizJustified({ questionId, slideIndex, optionId, justification, isCorrect }) {
      const submission = {
        type: 'labSubmit',
        missionId: 'lab.assess.quizJustified',
        slideIndex,
        membershipId: `mock-${userId}`,
        payload: { questionId, optionId, justification, isCorrect },
      }
      applyLabSubmissionLocally(submission)
      broadcast(submission)
      emit('quizJustifiedAck', { questionId, slideIndex, isCorrect, ts: Date.now() })
    },

    submitForm({ slideIndex, values }) {
      const submission = {
        type: 'labSubmit',
        missionId: 'lab.form.structured',
        slideIndex,
        membershipId: `mock-${userId}`,
        payload: { values },
      }
      applyLabSubmissionLocally(submission)
      broadcast(submission)
      emit('formAck', { slideIndex, fields: Object.keys(values), ts: Date.now() })
    },

    // ───────────────────────────────── postits (PR-3) ─────────────────────────────────
    createPostit({ slideIndex, text, columnId, anonymous }) {
      const postit: PostitEntry = {
        id: `mock-postit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        membershipId: `mock-${userId}`,
        slideIndex,
        columnId: columnId ?? null,
        text,
        positionIndex: (state.postitsBySlide[String(slideIndex)] ?? [])
          .filter((p) => p.columnId === (columnId ?? null)).length,
        anonymous: anonymous ?? false,
        deleted: false,
        reactionCount: 0,
        reactedBy: [],
      }
      const msg = { type: 'postitChange', action: 'created', postit }
      applyPostitChangeLocally(msg)
      broadcast(msg)
    },

    updatePostit({ postitId, text }) {
      const all = Object.values(state.postitsBySlide).flat()
      const p = all.find((x) => x.id === postitId)
      if (!p || p.membershipId !== `mock-${userId}`) return
      const updated: PostitEntry = { ...p, text }
      const msg = { type: 'postitChange', action: 'updated', postit: updated }
      applyPostitChangeLocally(msg)
      broadcast(msg)
    },

    movePostit({ postitId, toColumn }) {
      const all = Object.values(state.postitsBySlide).flat()
      const p = all.find((x) => x.id === postitId)
      if (!p) return
      const updated: PostitEntry = { ...p, columnId: toColumn }
      const msg = { type: 'postitChange', action: 'moved', postit: updated }
      applyPostitChangeLocally(msg)
      broadcast(msg)
    },

    deletePostit({ postitId }) {
      const all = Object.values(state.postitsBySlide).flat()
      const p = all.find((x) => x.id === postitId)
      if (!p) return
      const isOwner = p.membershipId === `mock-${userId}`
      if (!isOwner && role !== 'master') return
      const msg = { type: 'postitChange', action: 'deleted', postitId }
      applyPostitChangeLocally(msg)
      broadcast(msg)
    },

    reactPostit({ postitId }) {
      const all = Object.values(state.postitsBySlide).flat()
      const p = all.find((x) => x.id === postitId)
      if (!p) return
      const me = `mock-${userId}`
      const had = p.reactedBy.includes(me)
      const reactedBy = had ? p.reactedBy.filter((x) => x !== me) : [...p.reactedBy, me]
      const updated: PostitEntry = {
        ...p,
        reactedBy,
        reactionCount: reactedBy.length,
      }
      const msg = { type: 'postitChange', action: 'reacted', postit: updated }
      applyPostitChangeLocally(msg)
      broadcast(msg)
    },

    disconnect() {
      channel.close()
      listeners.clear()
    },
  }

  return adapter
}
